import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowSquareOut, CaretRight, CheckCircle, Fire, Plus, Ticket, WarningCircle, X } from '@phosphor-icons/react';
import { Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, SegmentedControl, ToastPill, useToastPill } from '../../../../components/immobilien/ui';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { useNutzerId } from '../../../../lib/immobilien/useNutzer';
import { istTicketOffen, ticketStatusLabel } from '../../../../lib/immobilien/tickets';
import type { TicketZeile } from '../../../../lib/immobilien/useObjektDetail';
import TicketFormular from '../../tickets/TicketFormular';
import { ladeFormularStammdaten, type FormularStammdaten } from './formularDaten';

type Filter = 'offen' | 'erledigt' | 'alle';

/**
 * Tickets des Objekts bzw. der Einheit (README §8). Öffnen führt in den Ticketbereich;
 * „Neues Ticket“ nutzt das bestehende Ticket-Formular, vorbelegt mit Objekt, Einheit und Mieter.
 */
export function TicketsBereich({ tickets, objektId, einheitId, mieterId, einheitName, onGeaendert }: {
  tickets: readonly TicketZeile[];
  objektId: string;
  einheitId?: string;
  mieterId?: string | null;
  /** Bezeichnung je Einheit (Objektansicht) */
  einheitName?: (id: string | null) => string | null;
  onGeaendert: () => void;
}) {
  const navigate = useNavigate();
  const nutzerId = useNutzerId();
  const [toast, zeige] = useToastPill();
  const [filter, setFilter] = useState<Filter>('offen');
  const [laedt, setLaedt] = useState(false);

  const offen = tickets.filter(t => istTicketOffen(t.status));
  const eskaliert = offen.filter(t => t.eskaliert || t.status === 'eskaliert');
  const hoch = offen.filter(t => ['hoch', 'kritisch'].includes((t.prioritaet ?? '').toLowerCase()));
  const sichtbar = filter === 'alle' ? tickets : filter === 'offen' ? offen : tickets.filter(t => !istTicketOffen(t.status));

  // Neues Ticket im neuen Design (gleiches Formular, Variante „immo“) direkt auf der Seite
  const [formular, setFormular] = useState<FormularStammdaten | null>(null);
  const neu = async () => {
    setLaedt(true);
    try {
      setFormular(await ladeFormularStammdaten());
    } catch (e) {
      zeige(`Formular konnte nicht geladen werden: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaedt(false);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <SegmentedControl hoehe={34} ariaLabel="Tickets filtern" wert={filter} onChange={setFilter}
          segmente={[{ key: 'offen', label: 'Offen', anzahl: offen.length }, { key: 'erledigt', label: 'Erledigt', anzahl: tickets.length - offen.length }, { key: 'alle', label: 'Alle', anzahl: tickets.length }]} />
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton groesse={40} icon={ArrowSquareOut} onClick={() => navigate('/admin/tickets')}>Ticketbereich</ImmoButton>
          <ImmoButton art="akzent" groesse={40} icon={Plus} disabled={laedt} onClick={() => { void neu(); }}>Neues Ticket</ImmoButton>
        </div>
      </div>
      {formular && (
        <div data-immo-ueberlagerung className="immo-side-in">
          <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
            <KartenTitel icon={Ticket} rechts={
              <button type="button" aria-label="Schließen" onClick={() => setFormular(null)} className="flex items-center justify-center cursor-pointer"
                style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><X size={14} weight="bold" /></button>
            }>Neues Ticket</KartenTitel>
            <TicketFormular variant="immo" kategorien={formular.kategorien} teamMembers={formular.teamMembers} teams={formular.teams}
              userProfile={nutzerId ? { id: nutzerId } : undefined}
              initialData={{ objekt_id: objektId, ...(einheitId ? { einheit_id: einheitId } : {}), ...(mieterId ? { mieter_id: mieterId } : {}) }}
              onClose={() => setFormular(null)} onCreated={() => { setFormular(null); zeige('Ticket angelegt'); onGeaendert(); }} />
          </InnenKarte>
        </div>
      )}
      <KpiReihe>
        <KpiTile label="Offen" wert={offen.length} sub={`${tickets.length} insgesamt`} icon={Ticket} ton="acc" />
        <KpiTile label="Eskaliert" wert={eskaliert.length} sub="brauchen Aufmerksamkeit" icon={WarningCircle} ton={eskaliert.length ? 'bad' : 'ok'} wertFarbig={eskaliert.length > 0} />
        <KpiTile label="Hohe Priorität" wert={hoch.length} sub="offen" icon={Fire} ton={hoch.length ? 'warn' : 'ok'} wertFarbig={hoch.length > 0} />
      </KpiReihe>
      {sichtbar.length === 0 ? (
        <LeerZustand icon={CheckCircle} titel={filter === 'offen' ? 'Keine offenen Tickets' : 'Keine Tickets'} />
      ) : (
        <div className="flex flex-col" style={{ gap: 8 }}>
          {sichtbar.map(t => {
            const offenT = istTicketOffen(t.status);
            const einheit = einheitName?.(t.einheit_id) ?? null;
            return (
              <button key={t.id} type="button" onClick={() => navigate(`/admin/tickets/${encodeURIComponent(t.id)}`)}
                className="immo-innen immo-hover-flaeche flex items-center text-left cursor-pointer flex-wrap"
                style={{ borderRadius: 18, padding: '12px 14px', gap: 12, border: 'none', color: 'var(--ink)', opacity: offenT ? 1 : 0.65 }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: offenT ? 'var(--acc-soft)' : 'var(--fill2)' }}>
                  <Ticket weight="fill" size={17} color={offenT ? 'var(--acc)' : 'var(--ink3)'} />
                </span>
                <span className="flex flex-col flex-1 min-w-0" style={{ flexBasis: 200 }}>
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{t.titel || 'Ticket'}</span>
                  <span className="truncate immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                    {[t.ticket_nummer, deDatum(t.created_at), einheit, t.faellig_am && offenT ? `fällig ${deDatum(t.faellig_am)}` : ''].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {(t.eskaliert || t.status === 'eskaliert') && offenT && <Chip groesse="xs" ton="bad">Eskaliert</Chip>}
                <Chip groesse="xs" ton={offenT ? 'acc' : 'ok'}>{ticketStatusLabel(t.status)}</Chip>
                <CaretRight size={14} color="var(--ink3)" />
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
